import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  LinkedTaskTemplateInput,
  TaskTemplate,
  TaskTemplateDependency,
  TASK_TEMPLATE_DEPENDENCIES,
  useGetTaskTemplatesQuery,
} from '../../../core/api/rtk/taskTemplatesApi';

interface LinkedTaskTemplateFieldProps {
  value: LinkedTaskTemplateInput[];
  onChange: (next: LinkedTaskTemplateInput[]) => void;
  excludeTaskId?: number;
  disabled?: boolean;
}

const DEFAULT_ROW: LinkedTaskTemplateInput = {
  id: 0,
  dependency: 'DoneBefore',
};

export const LinkedTaskTemplateField = ({
  value,
  onChange,
  excludeTaskId,
  disabled = false,
}: LinkedTaskTemplateFieldProps): JSX.Element => {
  const intl = useIntl();
  const { data, isLoading } = useGetTaskTemplatesQuery({ pageSize: 200 });
  const taskOptions: TaskTemplate[] = useMemo(() => {
    const rows = data?.results ?? [];
    return excludeTaskId ? rows.filter((task) => task.id !== excludeTaskId) : rows;
  }, [data, excludeTaskId]);
  const handleAddRow = (): void => {
    onChange([...value, { ...DEFAULT_ROW }]);
  };
  const handleRemoveRow = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };
  const handleRowChange = (index: number, patch: Partial<LinkedTaskTemplateInput>): void => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  return (
    <div>
      <button
        className="button"
        type="button"
        onClick={handleAddRow}
        disabled={disabled || isLoading}
      >
        {intl.formatMessage({ id: 'addLinkedTask', defaultMessage: 'Add linked task' })}
      </button>
      {value.map((row, index) => (
        <div
          key={`linked-row-${index}`}
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'flex-end',
            marginBottom: '0.5rem',
            marginTop: '0.5rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <label>
              {intl.formatMessage({ id: 'chooseDependency', defaultMessage: 'Dependency' })}
            </label>
            <select
              className="form-control"
              value={row.dependency}
              disabled={disabled}
              onChange={(e) =>
                handleRowChange(index, {
                  dependency: e.target.value as TaskTemplateDependency,
                })
              }
            >
              {TASK_TEMPLATE_DEPENDENCIES.map((dep) => (
                <option key={dep} value={dep}>
                  {dep}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 2 }}>
            <label>{intl.formatMessage({ id: 'chooseTask', defaultMessage: 'Task' })}</label>
            <select
              className="form-control"
              value={row.id || ''}
              disabled={disabled || isLoading}
              onChange={(e) => handleRowChange(index, { id: Number(e.target.value) || 0 })}
            >
              <option value="">
                {intl.formatMessage({ id: 'selectTask', defaultMessage: 'Select a task…' })}
              </option>
              {taskOptions.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.name}
                </option>
              ))}
            </select>
          </div>
          <button
            className="button"
            type="button"
            onClick={() => handleRemoveRow(index)}
            disabled={disabled}
            aria-label="Remove linked task"
          >
            <i className="icon icon-squared-cross" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default LinkedTaskTemplateField;
